import { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
	GetUploadEndpoint,
	MakeItemPublic,
	UploadToS3,
} from '../../../connection/uploads';
import { GetDefaultPasscodeList } from '../../../store/utils/create-event';
import { PASSCODE_LIST_TYPE } from '../../../types/working-model';
import GenerateExampleCsv from '../../../utils/generate-csv';
import LargeButton from '../../general-ui/button/large-button';
import RadioGroup from '../../general-ui/radio-group/radio-group';
import TextInput from '../../general-ui/text-input/text';
import { v4 as uuid } from 'uuid';
import { useSelector } from 'react-redux';
import { EventsState } from '../../../store/types';
import { SelectOption } from '../../general-ui/select/select';
import Modal from '../../general-ui/modal/modal';
import { CreateNewPasscodeList } from '../../../connection/passcode-lists';
import { getPasscodeLists } from '../../../store/actions/admin/passcode-lists';

interface PasscodeListProps {
	setModalClosedSubmit: () => void;
	setModalClosedCancel: () => void;
	modalOpen: boolean;
}

export interface WorkingPasscodeList {
	type: PASSCODE_LIST_TYPE;
	name: string;
	max_uses: number;
	master_passcode: string;
	url: string;
}

const passcodeListTypeOptions: SelectOption[] = [
	{
		label: 'Email only',
		value: PASSCODE_LIST_TYPE.EMAILS_ONLY,
	},
	{
		label: 'Passcode only',
		value: PASSCODE_LIST_TYPE.PASSCODES_ONLY,
	},
	{
		label: 'Both email and passcode',
		value: PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES,
	},
];

export default function PasscodeListCreator(
	props: PasscodeListProps
): JSX.Element {
	const { setModalClosedSubmit, setModalClosedCancel, modalOpen } = props;
	const {
		AuthReducer: { user, token },
	} = useSelector((state: EventsState) => state);
	const [workingPasscodeList, setWorkingPasscodeList] = useState<
		WorkingPasscodeList
	>(GetDefaultPasscodeList());
	const csvUpload = useRef<HTMLInputElement | null>(null);
	const [loading, setLoading] = useState(false);

	const dispatch = useDispatch();

	async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target?.files?.[0];

		if (file) {
			const endpoint = await GetUploadEndpoint(
				token!,
				`${user!.active_channel}/${uuid()}`,
				file.name.replace(/[^0-9a-zA-Z.]/g, '-')
			);
			await UploadToS3(
				endpoint.url,
				file,
				endpoint.fields
			);
			await MakeItemPublic(token!, endpoint.fields.Key);
			setWorkingPasscodeList((list: WorkingPasscodeList) => ({
				...list,
				url: endpoint.url + '/' + endpoint.fields.Key,
			}));
		}
	}

	async function onFile(file: File | FileList) {
		if (file instanceof File) {
			setLoading(true);
			const endpoint = await GetUploadEndpoint(
				token!,
				`${user!.active_channel}/${uuid()}`,
				file.name.replace(/[^0-9a-zA-Z.]/g, '-')
			);
			await UploadToS3(
				endpoint.url,
				file,
				endpoint.fields
			);
			await MakeItemPublic(token!, endpoint.fields.Key);
			setWorkingPasscodeList((list: WorkingPasscodeList) => ({
				...list,
				url: endpoint.url + '/' + endpoint.fields.Key,
			}));
			setLoading(false);
		}
	}

	function handlePasscodeListType(value: string | number) {
		switch (value) {
			case PASSCODE_LIST_TYPE.EMAILS_ONLY: {
				setWorkingPasscodeList((list: WorkingPasscodeList) => ({
					...list,
					type: PASSCODE_LIST_TYPE.EMAILS_ONLY,
				}));
				break;
			}
			case PASSCODE_LIST_TYPE.PASSCODES_ONLY: {
				setWorkingPasscodeList((list: WorkingPasscodeList) => ({
					...list,
					type: PASSCODE_LIST_TYPE.PASSCODES_ONLY,
				}));
				break;
			}
			case PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES: {
				setWorkingPasscodeList((list: WorkingPasscodeList) => ({
					...list,
					type: PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES,
				}));
				break;
			}
		}
	}

	function handlePasscodeListName(e: React.ChangeEvent<HTMLInputElement>) {
		setWorkingPasscodeList((list: WorkingPasscodeList) => ({
			...list,
			name: e.target.value,
		}));
	}

	function handlePasscodeListMasterPasscode(
		e: React.ChangeEvent<HTMLInputElement>
	) {
		setWorkingPasscodeList((list: WorkingPasscodeList) => ({
			...list,
			master_passcode: e.target.value,
		}));
	}

	// commenting out instead of deleting because passcodes is under active development
	// function handlePasscodeListMaxUses(e: React.ChangeEvent<HTMLInputElement>) {
	// 	setWorkingPasscodeList((list: WorkingPasscodeList) => ({
	// 		...list,
	// 		max_uses: Number(e.target.value),
	// 	}));
	// }

	async function finished() {
		setLoading(true);

		if (workingPasscodeList) {
			await CreateNewPasscodeList(
				token!,
				user!.active_channel,
				workingPasscodeList
			);
			dispatch(getPasscodeLists(user!.active_channel, token!));
		}
		setLoading(false);
		setWorkingPasscodeList(GetDefaultPasscodeList());
		setModalClosedSubmit();
	}

	return (
		<Modal
			open={modalOpen}
			onRequestClose={setModalClosedCancel}
			title={'Add List'}
			cancellable={true}
			closeable={false}
			footer={
				<>
					<button onClick={setModalClosedCancel} style={{ marginRight: 16 }}>
						Cancel
					</button>
					<button
						disabled={loading || workingPasscodeList.name === ''}
						onClick={finished}
						className="primary"
					>
						Apply
					</button>
				</>
			}
		>
			<div>
				<div
					style={{
						color: 'GrayText',
						fontSize: 14,
						marginBottom: 10,
					}}
				>
					A passcode list can be used give a group of people access to events and sessions.
				</div>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginTop: 16,
					}}
				></div>
				<div style={{ marginTop: 16, marginBottom: 16 }}>
					<div style={{ marginTop: 16, marginBottom: 16 }}>
						<TextInput
							label={'Passcode list name'}
							onChange={handlePasscodeListName}
							placeholder={'Passcode list name'}
						/>
						<TextInput
							label={'Master Passcode'}
							onChange={handlePasscodeListMasterPasscode}
							placeholder={'Enter master passcode'}
						/>
						<RadioGroup
							options={passcodeListTypeOptions}
							onChange={handlePasscodeListType}
							selected={workingPasscodeList?.type ?? 0}
							defaultValue={0}
							style={{ marginTop: 0 }}
						/>
					</div>
					<LargeButton
						title={'Upload a passcode list'}
						subtitle={'or drag and drop here in csv file format'}
						onFile={onFile}
						allowedFileTypes={[
							'text/csv',
							'application/vnd.ms-excel',
						]}
						multiple={false}
					/>
					<input
						type="file"
						onChange={handleFileChange}
						ref={csvUpload}
						accept=".csv"
						style={{ display: 'none' }}
					/>
					<button
						style={{ marginTop: 16 }}
						className="primary small"
						onClick={GenerateExampleCsv}
					>
						Download Example
					</button>
				</div>
			</div>
		</Modal>
	);
}
